import { ReactNode, createContext, useContext } from 'react';
import styled from 'styled-components';

import { focusVisible } from '../helpers';
import { palettes, tokens } from '../theme/tokens';
import { FlexGap } from './FlexGap';
import { StatusIndicator, StatusIndicatorState, statusIndicatorColor } from './StatusIndicator';

const statusSurface: Record<StatusIndicatorState, { background: string; border: string }> = {
	default: { background: tokens.info.background, border: statusIndicatorColor.default },
	success: { background: tokens.success.background, border: statusIndicatorColor.success },
	warning: { background: tokens.warning.background, border: statusIndicatorColor.warning },
	error: { background: tokens.error.background, border: statusIndicatorColor.error },
	off: { background: tokens.surface.white, border: tokens.border.default },
};

type ChoiceChipContextValue = {
	value?: string | null;
	disabled?: boolean;
	clearable?: boolean;
	onChange: (value: string | null) => void;
};

const ChoiceChipContext = createContext<ChoiceChipContextValue | null>(null);

type ChoiceChipGroupProps = {
	label: string;
	value?: string | null;
	disabled?: boolean;
	clearable?: boolean;
	onChange: (value: string | null) => void;
	children: ReactNode;
};

const Group = styled(FlexGap)`
	flex-wrap: wrap;
`;

function ChoiceChipGroup(props: ChoiceChipGroupProps) {
	return (
		<Group
			gap={tokens.gap.element}
			role="radiogroup"
			aria-label={props.label}
			aria-disabled={props.disabled || undefined}
		>
			<ChoiceChipContext.Provider
				value={{
					value: props.value,
					disabled: props.disabled,
					clearable: props.clearable,
					onChange: props.onChange,
				}}
			>
				{props.children}
			</ChoiceChipContext.Provider>
		</Group>
	);
}

function chipBorder(props: { $disabled?: boolean; $selected?: boolean; $accent?: string }) {
	if (props.$disabled && !props.$selected) {
		return palettes.heatherGray['200'];
	}

	if (props.$selected && props.$accent) {
		return props.$accent;
	}

	return tokens.border.default;
}

const Container = styled.button<{
	$disabled?: boolean;
	$selected?: boolean;
	$accent?: string;
	$fill?: string;
}>`
	display: inline-flex;
	align-items: center;
	box-sizing: border-box;
	height: 30px;
	margin: 0;
	padding: 0 ${tokens.padding.control}px;
	gap: ${tokens.gap.inline}px;
	border: 1px solid ${(props) => chipBorder(props)};
	border-radius: ${tokens.radius.full}px;
	background: ${(props) => props.$fill ?? tokens.surface.white};
	color: ${(props) =>
		props.$disabled && !props.$selected ? tokens.text.disabled : tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${(props) =>
		props.$selected ? tokens.typography.weights.semibold : tokens.typography.weights.medium};
	line-height: ${tokens.typography.lineHeights.heading};
	letter-spacing: 0.14px;
	white-space: nowrap;
	cursor: ${(props) => (props.$disabled ? 'default' : 'pointer')};
	appearance: none;
	${focusVisible}

	&:hover,
	&[data-preview='hover'] {
		background: ${(props) => props.$fill ?? tokens.surface.white};
		${(props) =>
			!props.$disabled && !props.$selected ? `border-color: ${tokens.border.strong};` : ''}
	}

	&:active,
	&[data-preview='pressed'] {
		background: ${(props) => props.$fill ?? tokens.surface.white};
	}

	&:disabled {
		cursor: default;
	}
`;

const Leading = styled.span<{ $faded?: boolean }>`
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	opacity: ${(props) => (props.$faded ? 0.5 : 1)};
`;

type ChoiceChipProps = {
	value: string;
	label: string;
	status?: StatusIndicatorState;
	disabled?: boolean;
	'data-preview'?: 'hover' | 'pressed';
};

export function ChoiceChip(props: ChoiceChipProps) {
	const group = useContext(ChoiceChipContext);
	const selected = group?.value === props.value;
	const disabled = props.disabled ?? group?.disabled ?? false;
	const surface = selected ? statusSurface[props.status ?? 'default'] : undefined;

	return (
		<Container
			type="button"
			role="radio"
			aria-checked={selected}
			disabled={disabled}
			$disabled={disabled}
			$selected={selected}
			$accent={surface?.border}
			$fill={surface?.background}
			data-preview={props['data-preview']}
			onClick={() => {
				if (!group || disabled) {
					return;
				}

				if (selected) {
					if (group.clearable) {
						group.onChange(null);
					}
					return;
				}

				group.onChange(props.value);
			}}
		>
			<Leading $faded={disabled && !selected}>
				<StatusIndicator state={props.status} />
			</Leading>
			{props.label}
		</Container>
	);
}

ChoiceChip.Group = ChoiceChipGroup;
